import { FC } from 'react';
import { format } from 'date-fns';

import { DialogContent, DialogClose } from '@app/shared/ui/dialog';
import { CrossIcon } from '@app/components/ui/icons';
import { Button } from '@app/shared/ui/button';
import { cvaIcon } from '@app/shared/ui/elements';
import { useTranslation } from '@app/app/i18n';

import { useGetUserInitQuery } from '@app/store/api/user.api';
import { useAppSelector } from '@app/hooks/use-store';

import {
  cvaCardContainer,
  cvaInnerContainer,
  cvaTitleContainer,
  cvaTitle,
  cvaSubtitle,
  cvaFrameContainer,
  cvaHelpText,
  cvaContactButton,
  cvaGradientBottom,
} from './payments-pro-cancelled-styles';

type PaymentsProCancelledProps = {
  onContactSupport?: () => void;
};

export const PaymentsProCancelled: FC<PaymentsProCancelledProps> = ({ onContactSupport }) => {
  const { isAuthenticated } = useAppSelector((state) => state.authState);
  const { data: userData } = useGetUserInitQuery(null, { skip: !isAuthenticated });
  const { t } = useTranslation('payments-dialog');

  const expirationDate = userData?.result?.product_balance?.expire;
  const supportEmail = 'support@wonderslide.com';

  const rawDate = expirationDate ? new Date(expirationDate) : null;
  const isValidDate = rawDate && !isNaN(rawDate.getTime());
  const formattedDate = isValidDate ? format(rawDate!, 'MMMM d, yyyy') : '—';

  return (
    <DialogContent
      className="max-w-[31.25rem] bg-transparent"
      customCloseIcon={
        <DialogClose
          aria-label={t('common.close')}
          className="absolute right-2 top-2 transition-colors disabled:pointer-events-none text-general_text_MII_secondary_i hover:text-general_text_MII_default focus-visible:outline"
        >
          <CrossIcon className={cvaIcon()} />
          <span className="sr-only">{t('common.close')}</span>
        </DialogClose>
      }
    >
      <div className={cvaCardContainer()}>
        <div className="flex flex-col items-center justify-center size-full">
          <div className={cvaInnerContainer()}>
            {/* Title section */}
            <div className={cvaTitleContainer()}>
              <h2 className={cvaTitle()}>{t('proCancelled.title')}</h2>
              <p className={cvaSubtitle()}>{t('proCancelled.subtitle', { date: formattedDate })}</p>
            </div>

            {/* Contact section */}
            <div className={cvaFrameContainer()}>
              <p className={cvaHelpText()}>{t('proCancelled.helpText')}</p>
              <Button
                onClick={onContactSupport}
                className={cvaContactButton()}
                variant="outline"
                borderRadius="3xl"
                size="semiMedium"
                asChild
              >
                <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
              </Button>
            </div>

            {/* Background gradient */}
            <div className={cvaGradientBottom()}>
              <div className="absolute inset-[-54.35%_-21.19%]">
                <svg
                  className="block size-full"
                  fill="none"
                  preserveAspectRatio="none"
                  viewBox="0 0 672 384"
                >
                  <g filter="url(#filter0_f_cancelled)" opacity="0.24">
                    <ellipse cx="336" cy="192" fill="#99218D" rx="236" ry="92" />
                  </g>
                  <defs>
                    <filter
                      colorInterpolationFilters="sRGB"
                      filterUnits="userSpaceOnUse"
                      height="384"
                      id="filter0_f_cancelled"
                      width="672"
                      x="0"
                      y="0"
                    >
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feBlend
                        in="SourceGraphic"
                        in2="BackgroundImageFix"
                        mode="normal"
                        result="shape"
                      />
                      <feGaussianBlur result="effect1_foregroundBlur" stdDeviation="50" />
                    </filter>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DialogContent>
  );
};
